'use client';

import { SvgCheck } from '@icons/designSystemIcons/SvgCheck';
import DynamicIcon from 'app/(web)/components/common/DynamicIcon';
import ProductBenefitsSection from 'app/(web)/components/common/ProductBenefitsSection';
import { useIsMobileDevice } from 'app/hooks/useIsMobileDevice';
import { ApplicationOrigin } from 'app/stores/globalStore';
import { Product, ProductType } from 'app/types/product';
import Carousel from 'designSystem/Carousel/Carousel';
import { Container, Flex } from 'designSystem/Layouts/Layouts';
import { Text, Title } from 'designSystem/Texts/Texts';
import Image from 'next/image';

import ProductSelectorButton from './ProductSelectorButton';
import { SURGERY_DATA } from './surgeryData';

export default function ProductSurgeryExplanation({
  product,
  showButton = true,
  moduleToShow = '',
  origin,
  isMobile,
}: {
  product: Product;
  showButton?: boolean;
  moduleToShow?: string;
  origin?: ApplicationOrigin;
  /** Lo calcula el Server Component padre con `isMobileSSR()`. */
  isMobile?: boolean;
}) {
  const detectedIsMobile = useIsMobileDevice();
  const isMobileDevice = isMobile ?? detectedIsMobile;

  const data = SURGERY_DATA.find(
    item => item.id.toUpperCase() === product.id.toUpperCase()
  );
  // La API devuelve también los pasos desactivados (Active = 0).
  const applicationZoneDetails = (
    product.extraInformation?.applicationZoneInfo?.applicationZoneDetail ?? []
  )
    .filter(detail => detail.active !== false)
    .sort((a, b) => a.order - b.order);
  if (!isMobileDevice) {
    return (
      <>
        {(!moduleToShow || moduleToShow == '1') && (
          <Container className="mt-8 justify-between px-0 md:px-4 md:flex md:flex-col md:pb-12 pdpExplanationTitle mt-[-20px]">
            <Container className="mt-8 justify-between py-8 px-0 md:px-4 md:flex md:flex-row md:items-start md:pb-4 pdpExplanationTitle">
              <Container className="mt-8 md:px-0 md:flex md:flex-col md:justify-start md:items-start">
                <Title isAnimated size="2xl" className="font-bold mb-6">
                  {data?.procedureTitle ??
                    (product.type == ProductType.Surgery
                      ? '¿En qué consiste la operación?'
                      : 'Procedimiento')}
                </Title>
                <p
                  className="text-hg-black500 mb-6"
                  dangerouslySetInnerHTML={{
                    __html: product.extraInformation?.procedimentDescription,
                  }}
                />
              </Container>
              <Container className="ml-5 md:px-0 md:flex md:flex-col md:justify-start md:items-start w-full">
                <Image
                  src={
                    '/images/pdp/' +
                    product.extraInformation!.slug +
                    '-surgery.jpg?v=5'
                  }
                  width={1000}
                  unoptimized
                  height={1000}
                  alt="Powering beauty"
                  className="w-full h-auto  mb-2 rounded-3xl"
                />
              </Container>
            </Container>
            <Text
              isAnimated
              className="ml-4 mb-4 text-hg-secondary font-gtUltra text-xldr"
            >
              {product.extraInformation?.benefitsInformation?.title}
            </Text>
            <Text
              isAnimated
              className="ml-4 text-hg-black500 mb-6 pdpExplanationDescription"
            >
              {product.extraInformation?.benefitsInformation?.description}
            </Text>
            <Container className="gap-16 justify-between py-8 px-0 md:px-4 md:flex md:flex-row md:items-start md:pb-4">
              {applicationZoneDetails.map((applicationZoneDetail, index) => (
                <Container
                  key={'application-zone-detail-' + index}
                  className={
                    'md:w-1/' +
                    applicationZoneDetails.length +
                    ' md:px-0 md:flex md:flex-col md:justify-start md:items-start'
                  }
                >
                  <div>
                    <Text isAnimated className="font-semibold mb-4">
                      {applicationZoneDetail.title}
                    </Text>
                    <Text isAnimated>{applicationZoneDetail.description}</Text>
                  </div>
                </Container>
              ))}
            </Container>
            {showButton && (
              <Flex
                layout="col-center"
                className="w-full px-4 py-8 md:px-0 md:py-12 md:pl-0 flex justify-center items-center"
              >
                <ProductSelectorButton
                  product={product}
                  ctaLanding={
                    origin == ApplicationOrigin.LandingSurgery ||
                    origin == ApplicationOrigin.Landing
                  }
                />
              </Flex>
            )}
          </Container>
        )}
        {product.title == 'Aumento de Pecho' && (
          <div className="bg-derma-secondary300 pt-12 pb-8 md:py-16">
            <Container>
              <Title
                isAnimated
                size="2xl"
                className="text-center mb-8 md:mb-12"
              >
                Tipos de implantes
              </Title>

              <div className="relative overflow-hidden flex justify-center items-center">
                <Carousel
                  hasDots={isMobileDevice}
                  hasControls={!isMobileDevice}
                  controlstyles="px-4"
                  className={`transition-all duration-500 `}
                  isIntrinsicHeight
                  visibleSlides={4}
                  infinite={false}
                  sliderStyles="md:gap-10 md:justify-center"
                >
                  <Flex
                    layout="col-left"
                    className="bg-derma-secondary400 p-8 rounded-2xl mx-4 preTripSlide items-center"
                  >
                    <DynamicIcon
                      className="h-12 w-12 mb-6 text-hg-secondary"
                      name={`IconoPecho1`}
                      family="surgery"
                    />
                    <Text className="text-center">
                      <b>Implantes anatómicos</b> Diseñados para imitar la forma
                      natural del pecho, con más volumen en la parte inferior,
                      creando un resultado más natural y suave.
                    </Text>
                  </Flex>
                  <Flex
                    layout="col-left"
                    className="bg-derma-secondary400 p-8 rounded-2xl mx-4 preTripSlide items-center"
                  >
                    <DynamicIcon
                      className="h-12 w-12 mb-6 text-hg-secondary"
                      name={`IconoPecho2`}
                      family="surgery"
                    />
                    <Text className="text-center">
                      <b>Implantes de pecho redondos</b> Ofrecen un mayor
                      volumen en la parte superior del pecho, ideales para
                      quienes buscan un escote más pronunciado y definido.
                    </Text>
                  </Flex>
                  <Flex
                    layout="col-left"
                    className="bg-derma-secondary400 p-8 rounded-2xl mx-4 preTripSlide items-center"
                  >
                    <DynamicIcon
                      className="h-12 w-12 mb-6 text-hg-secondary"
                      name={`IconoPecho3`}
                      family="surgery"
                    />
                    <Text className="text-center">
                      <b>Implantes de gel cohesivo</b> Fabricados con un gel de
                      silicona altamente cohesivo, que mantiene su forma incluso
                      en caso de rotura, ofreciendo un tacto natural y alta
                      seguridad.
                    </Text>
                  </Flex>
                  <Flex
                    layout="col-left"
                    className="bg-derma-secondary400 p-8 rounded-2xl mx-4 preTripSlide items-center"
                  >
                    <DynamicIcon
                      className="h-12 w-12 mb-6 text-hg-secondary"
                      name={`IconoPecho4`}
                      family="surgery"
                    />
                    <Text className="text-center">
                      <b>Implantes de solución salina</b> Menos comunes hoy en
                      día, están llenos de solución salina estéril y pueden ser
                      ajustados en tamaño durante la cirugía.
                    </Text>
                  </Flex>
                </Carousel>
              </div>
              {showButton && (
                <Flex
                  layout="col-center"
                  className="w-full px-4 py-2 md:px-0 md:py-2 md:pl-0 flex justify-center items-center"
                >
                  <ProductSelectorButton
                    product={product}
                    ctaLanding={origin == ApplicationOrigin.LandingSurgery}
                  />
                </Flex>
              )}
            </Container>
          </div>
        )}
        {(!moduleToShow || moduleToShow == '2') && (
          <ProductBenefitsSection
            product={product}
            benefitsMobileDifferent={product.type != ProductType.Surgery}
            benefitsTitles={data?.benefitsTitles}
            isMobile={isMobileDevice}
          >
            {showButton && (
              <ProductSelectorButton
                product={product}
                ctaLanding={
                  origin == ApplicationOrigin.LandingSurgery ||
                  origin == ApplicationOrigin.Landing
                }
              />
            )}
          </ProductBenefitsSection>
        )}
        {(!moduleToShow || moduleToShow == '3') && data && (
          <>
            <div className="bg-hg-cream300 w-full">
              <Container className="pt-20 pb-10 md:px-0 md:flex md:flex-col md:justify-start md:items-start w-full">
                <Title isAnimated size="2xl" className="font-bold mb-6">
                  {data.candidateTitle}
                </Title>
              </Container>
              <Container className="w-full md:px-0 flex flex-col md:flex-row md:items-start md:gap-16">
                <Container className="order-1 md:order-1 md:w-1/2 md:px-0 md:flex md:flex-col md:justify-start md:items-start">
                  {(data.candidateParagraphs ?? [data.candiateText]).map(
                    (paragraph, idx) => (
                      <Text
                        key={idx}
                        isAnimated
                        className="text-hg-black500 mb-6"
                      >
                        {paragraph}
                      </Text>
                    )
                  )}
                </Container>
                <Container className="order-2 md:order-2 md:w-1/2 md:px-0 md:flex md:flex-col md:justify-start md:items-start">
                  <ul className="flex flex-col gap-3">
                    {data.candidateBullets.map((item, idx) => (
                      <li
                        key={idx}
                        className="flex items-center gap-3 text-hg-black500 text-base"
                      >
                        <SvgCheck className="text-hg-secondary w-6 h-6 shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </Container>
              </Container>
              {product.type != ProductType.Surgery &&
                data.candidateNote !== '' && (
                  <Flex
                    layout="col-center"
                    className="px-4 mx-auto max-w-xl w-full md:px-0 flex flex-col md:flex-row md:items-start md:gap-16"
                  >
                    <Text isAnimated className="text-hg-black500">
                      {data.candidateNote ??
                        'Este procedimiento estético solo se realiza en personas mayores de 18 años. En Holaglow disponemos de un escáner facial 3D gratuito, que te permite visualizar el resultado de tu tratamiento antes de realizarlo, siempre acompañado del asesoramiento personalizado de nuestros especialistas.'}
                    </Text>
                  </Flex>
                )}
              {product.title.indexOf('IPL') == 0 && (
                <Flex
                  layout="col-center"
                  className="w-full pt-6 flex justify-center items-center"
                >
                  <Text isAnimated className="text-hg-black500">
                    Nuestros expertos en Holaglow realizan un análisis detallado
                    de tu piel con Escáner Facial 3D, garantizando un plan de
                    tratamiento adaptado a ti.
                  </Text>
                </Flex>
              )}
              {showButton && (
                <Flex
                  layout="col-center"
                  className="mb-10 w-full px-4 py-8 md:px-0 md:py-12 flex justify-center items-center"
                >
                  <ProductSelectorButton
                    product={product}
                    ctaLanding={
                      origin == ApplicationOrigin.LandingSurgery ||
                      origin == ApplicationOrigin.Landing
                    }
                  />
                </Flex>
              )}
            </div>
          </>
        )}
      </>
    );
  }
  return (
    <>
      {(!moduleToShow || moduleToShow == '1') && (
        <Container className="gap-16 justify-between px-0 md:px-4 md:flex md:flex-col md:pb-12 pdpExplanationTitle mt-[-20px]">
          <div className="relative w-full">
            <Image
              src={
                '/images/pdp/' +
                product.extraInformation!.slug +
                '-surgery.jpg?v=5'
              }
              width={1000}
              unoptimized
              height={1000}
              alt="Powering beauty"
              className="w-full h-auto  mb-2 rounded-3xl"
            />
          </div>
          <Container className="md:px-0 md:flex md:flex-col md:justify-start md:items-start w-full">
            <Title isAnimated size="2xl" className="font-bold mb-6">
              {data?.procedureTitle ??
                (product.type == ProductType.Surgery
                  ? '¿En qué consiste la operación?'
                  : 'Procedimiento')}
            </Title>
            <p
              className="text-hg-black500 mb-6"
              dangerouslySetInnerHTML={{
                __html: product.extraInformation?.procedimentDescription,
              }}
            />
            <Text
              isAnimated
              className="mb-4 text-hg-secondary font-gtUltra text-xldr"
            >
              {product.extraInformation?.benefitsInformation?.title}
            </Text>
            <Text
              isAnimated
              className="text-hg-black500 mb-6 pdpExplanationDescription"
            >
              {product.extraInformation?.benefitsInformation?.description}
            </Text>
            <ul className="flex flex-col mb-4 w-full">
              {applicationZoneDetails.map((applicationZoneDetail, index) => (
                <li
                  key={applicationZoneDetail.id}
                  className={` flex ${
                    applicationZoneDetails.length === index + 1
                      ? ''
                      : 'mb-6 pb-6 border-b border-hg-black'
                  }`}
                >
                  <div>
                    <Text isAnimated className="font-semibold mb-4">
                      {applicationZoneDetail.title}
                    </Text>
                    <Text isAnimated>{applicationZoneDetail.description}</Text>
                  </div>
                </li>
              ))}
            </ul>
          </Container>
          {showButton && (
            <Flex
              layout="col-center"
              className="w-full px-4 py-8 md:px-0 md:py-12 md:pl-0 flex justify-center items-center"
            >
              <ProductSelectorButton
                product={product}
                ctaLanding={
                  origin == ApplicationOrigin.LandingSurgery ||
                  origin == ApplicationOrigin.Landing
                }
              />
            </Flex>
          )}
        </Container>
      )}
      {product.title == 'Aumento de Pecho' && (
        <div className="bg-derma-secondary300 pt-12 pb-8 md:py-16">
          <Container>
            <Title isAnimated size="2xl" className="text-center mb-8 md:mb-12">
              Tipos de implantes
            </Title>

            <div className="relative overflow-hidden flex justify-center items-center">
              <Carousel
                hasDots={isMobileDevice}
                hasControls={!isMobileDevice}
                controlstyles="px-4 py-4"
                className={`transition-all duration-500 `}
                isIntrinsicHeight
                visibleSlides={1}
                infinite={false}
                sliderStyles="md:gap-10 md:justify-center"
              >
                <Flex
                  layout="col-left"
                  className="bg-derma-secondary400 p-8 rounded-2xl mx-4 preTripSlide items-center"
                >
                  <DynamicIcon
                    className="h-12 w-12 mb-6 text-hg-secondary"
                    name={`IconoPecho1`}
                    family="surgery"
                  />
                  <Text className="text-center">
                    <b>Implantes anatómicos</b> Diseñados para imitar la forma
                    natural del pecho, con más volumen en la parte inferior,
                    creando un resultado más natural y suave.
                  </Text>
                </Flex>
                <Flex
                  layout="col-left"
                  className="bg-derma-secondary400 p-8 rounded-2xl mx-4 preTripSlide items-center"
                >
                  <DynamicIcon
                    className="h-12 w-12 mb-6 text-hg-secondary"
                    name={`IconoPecho2`}
                    family="surgery"
                  />
                  <Text className="text-center">
                    <b>Implantes de pecho redondos</b> Ofrecen un mayor volumen
                    en la parte superior del pecho, ideales para quienes buscan
                    un escote más pronunciado y definido.
                  </Text>
                </Flex>
                <Flex
                  layout="col-left"
                  className="bg-derma-secondary400 p-8 rounded-2xl mx-4 preTripSlide items-center"
                >
                  <DynamicIcon
                    className="h-12 w-12 mb-6 text-hg-secondary"
                    name={`IconoPecho3`}
                    family="surgery"
                  />
                  <Text className="text-center">
                    <b>Implantes de gel cohesivo</b> Fabricados con un gel de
                    silicona altamente cohesivo, que mantiene su forma incluso
                    en caso de rotura, ofreciendo un tacto natural y alta
                    seguridad.
                  </Text>
                </Flex>
                <Flex
                  layout="col-left"
                  className="bg-derma-secondary400 p-8 rounded-2xl mx-4 preTripSlide items-center"
                >
                  <DynamicIcon
                    className="h-12 w-12 mb-6 text-hg-secondary"
                    name={`IconoPecho4`}
                    family="surgery"
                  />
                  <Text className="text-center">
                    <b>Implantes de solución salina</b> Menos comunes hoy en
                    día, están llenos de solución salina estéril y pueden ser
                    ajustados en tamaño durante la cirugía.
                  </Text>
                </Flex>
              </Carousel>
            </div>
            {showButton && (
              <Flex
                layout="col-center"
                className="w-full px-4 py-8 md:px-0 md:py-8 md:pl-0 flex justify-center items-center"
              >
                <ProductSelectorButton
                  product={product}
                  ctaLanding={
                    origin == ApplicationOrigin.LandingSurgery ||
                    origin == ApplicationOrigin.Landing
                  }
                />
              </Flex>
            )}
          </Container>
        </div>
      )}
      {(!moduleToShow || moduleToShow == '2') && (
        <ProductBenefitsSection
          product={product}
          benefitsMobileDifferent={product.type != ProductType.Surgery}
          benefitsTitles={data?.benefitsTitles}
          isMobile={isMobileDevice}
        >
          {showButton && (
            <ProductSelectorButton
              product={product}
              ctaLanding={
                origin == ApplicationOrigin.LandingSurgery ||
                origin == ApplicationOrigin.Landing
              }
            />
          )}
        </ProductBenefitsSection>
      )}
      {(!moduleToShow || moduleToShow == '3') && data && (
        <>
          <div className="bg-hg-cream300 w-full">
            <Container className="pt-10 md:px-0 md:flex md:flex-col md:justify-start md:items-start w-full">
              <Title isAnimated size="2xl" className="font-bold mb-6">
                {data.candidateTitle}
              </Title>
            </Container>
            <Container className="w-full md:px-0 flex flex-col md:flex-row md:items-start md:gap-16">
              <Container className="order-1 md:order-1 md:w-1/2 md:px-0 md:flex md:flex-col md:justify-start md:items-start">
                {(data.candidateParagraphs ?? [data.candiateText]).map(
                  (paragraph, idx) => (
                    <Text
                      key={idx}
                      isAnimated
                      className="text-hg-black500 mb-6"
                    >
                      {paragraph}
                    </Text>
                  )
                )}
              </Container>
              <Container className="order-2 md:order-2 md:w-1/2 md:px-0 md:flex md:flex-col md:justify-start md:items-start">
                <ul className="flex flex-col gap-3">
                  {data.candidateBullets.map((item, idx) => (
                    <li
                      key={idx}
                      className="flex items-center gap-3 text-hg-black500 text-base"
                    >
                      <SvgCheck className="text-hg-secondary w-6 h-6 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </Container>
            </Container>

            {product.type != ProductType.Surgery &&
              data.candidateNote !== '' && (
                <Flex
                  layout="col-center"
                  className="mt-5 px-4 mx-auto max-w-xl w-full md:px-0 flex flex-col md:flex-row md:items-start md:gap-16"
                >
                  <Text isAnimated className="text-hg-black500">
                    {data.candidateNote ??
                      'Este procedimiento estético solo se realiza en personas mayores de 18 años. En Holaglow disponemos de un escáner facial 3D gratuito, que te permite visualizar el resultado de tu tratamiento antes de realizarlo, siempre acompañado del asesoramiento personalizado de nuestros especialistas.'}
                  </Text>
                </Flex>
              )}
            {product.title.indexOf('IPL') == 0 && (
              <Flex
                layout="col-center"
                className="w-full px-4 py-8 md:px-0 md:py-12 flex justify-center items-center"
              >
                <Text isAnimated className="text-hg-black500">
                  Nuestros expertos en Holaglow realizan un análisis detallado
                  de tu piel con Escáner Facial 3D, garantizando un plan de
                  tratamiento adaptado a ti.
                </Text>
              </Flex>
            )}
            {showButton && (
              <Flex
                layout="col-center"
                className="w-full px-4 py-8 md:px-0 md:py-12 flex justify-center items-center"
              >
                <ProductSelectorButton
                  product={product}
                  ctaLanding={
                    origin == ApplicationOrigin.LandingSurgery ||
                    origin == ApplicationOrigin.Landing
                  }
                />
              </Flex>
            )}
          </div>
        </>
      )}
    </>
  );
}
